import { useEffect, useMemo } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { useUserAccess } from '../../hooks/useUserAccess';
import {
  DataPane,
  EncodingShelves,
  QueryToolbar,
  QueryResults,
  SaveReportDialog,
  ReportLibrary,
  ChartConfigPanel,
} from '../../components/report-builder';
import type { ScopingType } from '../../types/reportBuilder';

export default function ReportBuilderPage() {
  const { fetchModels, setScopingType, loadSavedReports, viewMode, selectedModel } = useReportBuilderStore();
  const { isSuperAdmin, isGlobalHRBP, isScopedHRBP, isExecutive, isManager } = useUserAccess();

  const scopingType: ScopingType = useMemo(() => {
    if (isSuperAdmin) return 'client_admin';
    if (isGlobalHRBP) return 'global_hr';
    if (isScopedHRBP) return 'scoped_hr';
    if (isExecutive) return 'executive';
    if (isManager) return 'manager';
    return 'self';
  }, [isSuperAdmin, isGlobalHRBP, isScopedHRBP, isExecutive, isManager]);

  useEffect(() => {
    setScopingType(scopingType);
  }, [scopingType, setScopingType]);

  useEffect(() => {
    fetchModels();
    loadSavedReports();
  }, [fetchModels, loadSavedReports]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Top toolbar */}
      <header className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-2">
        <QueryToolbar />
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Data Pane (models + fields) */}
        <aside className="w-64 flex-shrink-0 overflow-hidden border-r border-gray-200 bg-gray-50/80">
          <DataPane />
        </aside>

        {/* Center: Shelves + Canvas */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Encoding shelves strip */}
          {selectedModel && (
            <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-2">
              <EncodingShelves />
            </div>
          )}

          {/* Canvas */}
          <main className="flex-1 overflow-auto bg-gray-50 p-4">
            <QueryResults />
          </main>

          {/* Report library at bottom of center */}
          <ReportLibrary />
        </div>

        {/* Right sidebar - Chart config (shown only in chart view) */}
        {viewMode === 'chart' && (
          <aside className="w-72 flex-shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-3">
            <ChartConfigPanel />
          </aside>
        )}
      </div>

      {/* Save dialog (portal modal) */}
      <SaveReportDialog />
    </div>
  );
}
